import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Construction, Download, FileUp, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { type BatchRecord, BatchUploadForm } from "@/components/batch/batch-upload-form";
import { BatchScopePicker } from "@/components/dashboard-upload/batch-scope-picker";
import {
  type StudentExportRow,
  studentsToCsv,
  triggerCsvDownload,
} from "@/components/dashboard-upload/csv-template";
import {
  ENTITY_REGISTRY,
  type EntityRegistryEntry,
  entityByKey,
} from "@/components/dashboard-upload/registry";
import { ReportcardCsvMode } from "@/components/dashboard-upload/reportcard-bulk-import";
import { ReportcardScopePicker } from "@/components/dashboard-upload/reportcard-scope-picker";
import { StudentCsvMode } from "@/components/dashboard-upload/student-bulk-import";
import { DataGrid } from "@/components/dashboard/data-grid";
import { PaginationBar } from "@/components/dashboard/pagination-bar";
import { SearchBox } from "@/components/dashboard/search-box";
import { type ExamRecord, ExamUploadForm } from "@/components/exam/exam-upload-form";
import { ConfirmDialog } from "@/components/forms/confirm-dialog";
import { formatPbError } from "@/components/forms/form-helpers";
import { NewsUploadForm } from "@/components/news/news-upload-form";
import { PageHeader } from "@/components/page-header";
import { type StudentRecord, StudentUploadForm } from "@/components/student/student-upload-form";
import { type TeacherRecord, TeacherUploadForm } from "@/components/teacher/teacher-upload-form";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { FieldDef, SchemaRecord } from "@/lib/dashboard/types";
import { useEntityTable } from "@/lib/dashboard/use-entity-table";
import type { NewsRecord } from "@/lib/news-helpers";
import { pb } from "@/lib/pb";
import { useDocumentTitle } from "@/lib/use-document-title";
import { cn } from "@/lib/utils";

/**
 * Single NEET workflow surface — schema-driven, OSS-extractable substrate
 * + per-entity NEET overlays. The entire support workflow lives at this
 * one route; entity tabs in the left sidebar swap the active workspace.
 *
 * Phase A (this commit): every workspace fetches 25 rows per page via
 * useEntityTable (server-side sort + search + pagination); Exam and
 * Student now require a batch selection before fetching, mirroring the
 * Reportcard tab. Tab switches serve cached data until a write
 * invalidates that entity's queries.
 */
const searchSchema = z.object({
  entity: z.string().optional(),
  batch: z.string().optional(),
  exam: z.string().optional(),
  // Deep-link target: student id whose edit sheet should auto-open (used by
  // the "Edit details" link on the student detail page).
  student: z.string().optional(),
});

export const Route = createFileRoute("/_app/_support/dashboard-upload/")({
  validateSearch: searchSchema,
  component: DashboardUploadPage,
});

function DashboardUploadPage() {
  useDocumentTitle("Upload dashboard");
  const { entity, batch, exam } = Route.useSearch();
  const navigate = Route.useNavigate();

  const activeKey =
    entity ?? ENTITY_REGISTRY.find((e) => e.status === "active")?.key ?? "reportcard";
  const active = entityByKey(activeKey);

  function selectEntity(key: string) {
    void navigate({ search: { entity: key }, replace: true });
  }

  function setBatch(next: string | undefined) {
    void navigate({
      search: (prev) => ({ ...prev, ...(next ? { batch: next } : { batch: undefined }) }),
      replace: true,
    });
  }

  function setExam(next: string | undefined) {
    void navigate({
      search: (prev) => ({ ...prev, ...(next ? { exam: next } : { exam: undefined }) }),
      replace: true,
    });
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
      <PageHeader
        eyebrow="Admin"
        title="Upload dashboard"
        meta="One workflow surface for the entire NEET application. Manage records, scope by batch/exam, bulk-import from CSV."
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
        <aside className="h-fit rounded-xl border border-border/60 bg-card p-3 shadow-[var(--shadow-bento)]">
          <Sidebar activeKey={activeKey} onSelect={selectEntity} />
        </aside>

        <section className="flex min-w-0 flex-col gap-3">
          {active ? (
            <Workspace
              entry={active}
              batchId={batch}
              examId={exam}
              onBatchChange={setBatch}
              onExamChange={setExam}
            />
          ) : (
            <ComingSoon label={activeKey} />
          )}
        </section>
      </div>
    </div>
  );
}

function Sidebar({
  activeKey,
  onSelect,
}: {
  activeKey: string;
  onSelect: (key: string) => void;
}) {
  const groups = { core: [] as EntityRegistryEntry[], "auth-linked": [] as EntityRegistryEntry[] };
  for (const e of ENTITY_REGISTRY) groups[e.group].push(e);

  return (
    <nav aria-label="Upload dashboard entities" className="flex flex-col gap-3">
      {(["core", "auth-linked"] as const).map((g) => (
        <div key={g}>
          <div className="text-muted-foreground mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider">
            {g === "core" ? "Core" : "Auth-linked"}
          </div>
          <ul className="flex flex-col">
            {groups[g].map((e) => {
              const isActive = e.key === activeKey;
              return (
                <li key={e.key}>
                  <button
                    type="button"
                    onClick={() => onSelect(e.key)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                      isActive
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
                    )}
                  >
                    <span>{e.label}</span>
                    {e.status === "pending" && (
                      <span className="text-muted-foreground text-[9px] font-semibold uppercase tracking-wider">
                        soon
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function Workspace({
  entry,
  batchId,
  examId,
  onBatchChange,
  onExamChange,
}: {
  entry: EntityRegistryEntry;
  batchId: string | undefined;
  examId: string | undefined;
  onBatchChange: (id: string | undefined) => void;
  onExamChange: (id: string | undefined) => void;
}) {
  if (!entry.collection || !entry.fields) {
    return (
      <div className="text-muted-foreground rounded-xl border border-dashed p-8 text-center text-sm">
        Entity registered without a collection/schema — registry config error.
      </div>
    );
  }

  if (entry.key === "reportcard") {
    return (
      <ReportcardWorkspace
        fields={entry.fields}
        batchId={batchId}
        examId={examId}
        onBatchChange={onBatchChange}
        onExamChange={onExamChange}
      />
    );
  }

  if (entry.key === "batch") return <BatchWorkspace fields={entry.fields} />;

  if (entry.key === "exam") {
    return <ExamWorkspace fields={entry.fields} batchId={batchId} onBatchChange={onBatchChange} />;
  }

  if (entry.key === "news") return <NewsWorkspace fields={entry.fields} />;

  if (entry.key === "teacher") return <TeacherWorkspace fields={entry.fields} />;

  if (entry.key === "student") {
    return (
      <StudentWorkspace fields={entry.fields} batchId={batchId} onBatchChange={onBatchChange} />
    );
  }

  return (
    <div className="text-muted-foreground rounded-xl border border-dashed p-8 text-center text-sm">
      No workspace registered for entity “{entry.key}”.
    </div>
  );
}

function ComingSoon({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border/60 bg-card p-12 text-center shadow-[var(--shadow-bento)]">
      <Construction className="text-muted-foreground h-8 w-8" />
      <div className="space-y-1">
        <p className="text-sm font-semibold tracking-tight">{label} — coming soon</p>
        <p className="text-muted-foreground text-xs">
          This entity hasn't been wired up in the dashboard yet.
        </p>
      </div>
    </div>
  );
}

function GatedNotice({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-border/60 bg-surface-muted/40 p-12 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

interface ReportcardRow extends SchemaRecord {
  id: string;
  student_name?: string;
  exam_name?: string;
}

function ReportcardWorkspace({
  fields,
  batchId,
  examId,
  onBatchChange,
  onExamChange,
}: {
  fields: FieldDef[];
  batchId: string | undefined;
  examId: string | undefined;
  onBatchChange: (id: string | undefined) => void;
  onExamChange: (id: string | undefined) => void;
}) {
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<ReportcardRow | null>(null);
  const [csvMode, setCsvMode] = useState(false);

  // Mounting CsvMode unmounts the PB table tree below, so this hook
  // unmounts too — react-query stops listening for the
  // report_card_combined key while CSV import is in flight.
  const table = useEntityTable<ReportcardRow>({
    collection: "report_card_combined",
    extraFilter: examId ? `exam_id = '${examId}'` : undefined,
    defaultSort: { field: "rank", dir: "asc" },
    enabled: Boolean(examId),
  });

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await pb.collection("report_card").delete(pendingDelete.id);
      toast.success(`Removed report card for ${pendingDelete.student_name ?? "this student"}`);
      await table.invalidate();
    } catch (err) {
      toast.error("Could not delete report card", { description: formatPbError(err) });
    }
  }

  if (csvMode && batchId && examId) {
    return (
      <>
        <ReportcardScopePicker
          batchId={batchId}
          examId={examId}
          onBatchChange={onBatchChange}
          onExamChange={onExamChange}
        />
        <ReportcardCsvMode
          batchId={batchId}
          examId={examId}
          onDiscard={() => setCsvMode(false)}
          onCommitted={async () => {
            // Invalidate before unmounting CsvMode so the PB tree's next
            // mount kicks off a single refetch with fresh data.
            await queryClient.invalidateQueries({
              queryKey: ["dashboard-upload", "report_card_combined"],
            });
            setCsvMode(false);
          }}
        />
      </>
    );
  }

  return (
    <>
      <ReportcardScopePicker
        batchId={batchId}
        examId={examId}
        onBatchChange={onBatchChange}
        onExamChange={onExamChange}
      />

      {!examId ? (
        <GatedNotice>Pick a batch and exam above to load report cards.</GatedNotice>
      ) : (
        <>
          <div className="flex justify-end">
            <Button
              variant="default"
              size="sm"
              onClick={() => setCsvMode(true)}
              disabled={!batchId}
              className="gap-2"
            >
              <FileUp className="h-3.5 w-3.5" />
              Import from CSV
            </Button>
          </div>
          <DataGrid
            fields={fields}
            records={table.records}
            loading={table.isLoading}
            currentSort={table.sort}
            onSortChange={table.setSort}
            hideFooter
            emptyHint="No report cards for this exam yet."
            rowActions={(record) => (
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete report card for ${(record as ReportcardRow).student_name ?? "this student"}`}
                onClick={() => setPendingDelete(record as ReportcardRow)}
                className="text-muted-foreground hover:text-destructive h-7 w-7"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          />
          <PaginationBar
            page={table.page}
            perPage={table.perPage}
            total={table.total}
            onPageChange={table.setPage}
            disabled={table.isFetching}
          />

          <ConfirmDialog
            open={pendingDelete !== null}
            onOpenChange={(next) => {
              if (!next) setPendingDelete(null);
            }}
            title="Delete this report card?"
            description={
              <>
                This removes the report card for{" "}
                <span className="text-foreground font-medium">
                  {pendingDelete?.student_name ?? "this student"}
                </span>
                . Their individual subject mark records will cascade-delete with it. This cannot be
                undone.
              </>
            }
            confirmLabel="Delete report card"
            onConfirm={handleDelete}
          />
        </>
      )}
    </>
  );
}

interface BatchRow extends SchemaRecord {
  id: string;
  name: string;
}

function BatchWorkspace({ fields }: { fields: FieldDef[] }) {
  const table = useEntityTable<BatchRow>({
    collection: "batch",
    searchField: "name",
    defaultSort: { field: "created", dir: "desc" },
  });

  const [sheetMode, setSheetMode] = useState<
    "create" | { mode: "edit"; record: BatchRecord } | null
  >(null);
  const [pendingDelete, setPendingDelete] = useState<BatchRow | null>(null);

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await pb.collection("batch").delete(pendingDelete.id);
      toast.success(`Batch [${pendingDelete.name}] deleted`);
      await table.invalidate();
    } catch (err) {
      toast.error("Could not delete batch", { description: formatPbError(err) });
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SearchBox
          value={table.query}
          onChange={table.setQuery}
          placeholder="Search batches by name…"
          className="w-full max-w-sm"
        />
        <Button size="sm" onClick={() => setSheetMode("create")} className="gap-2">
          <Plus className="h-3.5 w-3.5" />
          New batch
        </Button>
      </div>
      <DataGrid
        fields={fields}
        records={table.records}
        loading={table.isLoading}
        currentSort={table.sort}
        onSortChange={table.setSort}
        hideFooter
        emptyHint={table.query ? "No batches match this search." : "No batches yet."}
        rowActions={(record) => {
          const row = record as BatchRow;
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Edit ${row.name}`}
                onClick={() =>
                  setSheetMode({ mode: "edit", record: row as unknown as BatchRecord })
                }
                className="text-muted-foreground hover:text-foreground h-7 w-7"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete ${row.name}`}
                onClick={() => setPendingDelete(row)}
                className="text-muted-foreground hover:text-destructive h-7 w-7"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          );
        }}
      />
      <PaginationBar
        page={table.page}
        perPage={table.perPage}
        total={table.total}
        onPageChange={table.setPage}
        disabled={table.isFetching}
      />

      <Sheet
        open={sheetMode !== null}
        onOpenChange={(next) => {
          if (!next) setSheetMode(null);
        }}
      >
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{sheetMode === "create" ? "New batch" : `Edit batch`}</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <BatchUploadForm
              {...(sheetMode && sheetMode !== "create" ? { b: sheetMode.record } : {})}
              onSaved={async () => {
                await table.invalidate();
                setSheetMode(null);
              }}
              onDeleted={async () => {
                await table.invalidate();
                setSheetMode(null);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null);
        }}
        title="Delete this batch?"
        description={
          <>
            This removes <span className="text-foreground font-medium">{pendingDelete?.name}</span>.
            Students, exams, and report cards linked to this batch will need to be cleaned up
            separately (no cascade). This cannot be undone.
          </>
        }
        confirmLabel="Delete batch"
        onConfirm={handleDelete}
      />
    </>
  );
}

interface ExamRow extends SchemaRecord {
  id: string;
  name: string;
  batch?: string;
}

function ExamWorkspace({
  fields,
  batchId,
  onBatchChange,
}: {
  fields: FieldDef[];
  batchId: string | undefined;
  onBatchChange: (id: string | undefined) => void;
}) {
  const table = useEntityTable<ExamRow>({
    collection: "exam",
    extraFilter: batchId ? `batch = '${batchId}'` : undefined,
    searchField: "name",
    defaultSort: { field: "date", dir: "desc" },
    enabled: Boolean(batchId),
  });

  const [sheetMode, setSheetMode] = useState<
    "create" | { mode: "edit"; record: ExamRecord } | null
  >(null);
  const [pendingDelete, setPendingDelete] = useState<ExamRow | null>(null);

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await pb.collection("exam").delete(pendingDelete.id);
      toast.success(`Exam [${pendingDelete.name}] deleted`);
      await table.invalidate();
    } catch (err) {
      toast.error("Could not delete exam", { description: formatPbError(err) });
    }
  }

  return (
    <>
      <BatchScopePicker batchId={batchId} onBatchChange={onBatchChange} label="Scope by batch" />

      {!batchId ? (
        <GatedNotice>Pick a batch above to load exams.</GatedNotice>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SearchBox
              value={table.query}
              onChange={table.setQuery}
              placeholder="Search exams by name…"
              className="w-full max-w-sm"
            />
            <Button size="sm" onClick={() => setSheetMode("create")} className="gap-2">
              <Plus className="h-3.5 w-3.5" />
              New exam
            </Button>
          </div>
          <DataGrid
            fields={fields}
            records={table.records}
            loading={table.isLoading}
            currentSort={table.sort}
            onSortChange={table.setSort}
            hideFooter
            emptyHint={table.query ? "No exams match this search." : "No exams in this batch yet."}
            rowActions={(record) => {
              const row = record as ExamRow;
              return (
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${row.name}`}
                    onClick={() =>
                      setSheetMode({ mode: "edit", record: row as unknown as ExamRecord })
                    }
                    className="text-muted-foreground hover:text-foreground h-7 w-7"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${row.name}`}
                    onClick={() => setPendingDelete(row)}
                    className="text-muted-foreground hover:text-destructive h-7 w-7"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            }}
          />
          <PaginationBar
            page={table.page}
            perPage={table.perPage}
            total={table.total}
            onPageChange={table.setPage}
            disabled={table.isFetching}
          />

          <Sheet
            open={sheetMode !== null}
            onOpenChange={(next) => {
              if (!next) setSheetMode(null);
            }}
          >
            <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
              <SheetHeader>
                <SheetTitle>{sheetMode === "create" ? "New exam" : "Edit exam"}</SheetTitle>
              </SheetHeader>
              <div className="mt-4">
                <ExamUploadForm
                  {...(sheetMode && sheetMode !== "create" ? { e: sheetMode.record } : {})}
                  {...(sheetMode === "create" && batchId ? { defaultBatchId: batchId } : {})}
                  onSaved={async () => {
                    await table.invalidate();
                    setSheetMode(null);
                  }}
                  onDeleted={async () => {
                    await table.invalidate();
                    setSheetMode(null);
                  }}
                />
              </div>
            </SheetContent>
          </Sheet>

          <ConfirmDialog
            open={pendingDelete !== null}
            onOpenChange={(next) => {
              if (!next) setPendingDelete(null);
            }}
            title="Delete this exam?"
            description={
              <>
                This removes{" "}
                <span className="text-foreground font-medium">{pendingDelete?.name}</span>. Report
                cards and per-subject marks linked to this exam will need to be cleaned up
                separately (no cascade). This cannot be undone.
              </>
            }
            confirmLabel="Delete exam"
            onConfirm={handleDelete}
          />
        </>
      )}
    </>
  );
}

interface NewsRow extends SchemaRecord {
  id: string;
  title: string;
  type?: string;
}

const NEWS_TYPE_FILTERS = ["all", "global", "batches", "teachers", "students"] as const;

function NewsWorkspace({ fields }: { fields: FieldDef[] }) {
  const [typeFilter, setTypeFilter] = useState<(typeof NEWS_TYPE_FILTERS)[number]>("all");

  const table = useEntityTable<NewsRow>({
    collection: "news",
    extraFilter: typeFilter !== "all" ? `type = '${typeFilter}'` : undefined,
    searchField: "title",
    defaultSort: { field: "created", dir: "desc" },
  });

  const [sheetMode, setSheetMode] = useState<
    "create" | { mode: "edit"; record: NewsRecord } | null
  >(null);
  const [pendingDelete, setPendingDelete] = useState<NewsRow | null>(null);

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await pb.collection("news").delete(pendingDelete.id);
      toast.success(`News "${pendingDelete.title}" deleted`);
      await table.invalidate();
    } catch (err) {
      toast.error("Could not delete news", { description: formatPbError(err) });
    }
  }

  return (
    <>
      <div className="sticky top-20 z-20 flex flex-wrap items-center gap-2 rounded-xl border border-border/60 bg-card p-3 shadow-[var(--shadow-bento)]">
        <span className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
          Audience
        </span>
        {NEWS_TYPE_FILTERS.map((t) => {
          const active = typeFilter === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTypeFilter(t)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                active
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border bg-background text-muted-foreground hover:bg-accent/30 hover:text-foreground",
              )}
            >
              {t}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SearchBox
          value={table.query}
          onChange={table.setQuery}
          placeholder="Search news by title…"
          className="w-full max-w-sm"
        />
        <Button size="sm" onClick={() => setSheetMode("create")} className="gap-2">
          <Plus className="h-3.5 w-3.5" />
          New news
        </Button>
      </div>
      <DataGrid
        fields={fields}
        records={table.records}
        loading={table.isLoading}
        currentSort={table.sort}
        onSortChange={table.setSort}
        hideFooter
        emptyHint={
          table.query
            ? "No news match this search."
            : typeFilter === "all"
              ? "No news yet."
              : `No news targeting ${typeFilter}.`
        }
        rowActions={(record) => {
          const row = record as NewsRow;
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Edit ${row.title}`}
                onClick={() => setSheetMode({ mode: "edit", record: row as unknown as NewsRecord })}
                className="text-muted-foreground hover:text-foreground h-7 w-7"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete ${row.title}`}
                onClick={() => setPendingDelete(row)}
                className="text-muted-foreground hover:text-destructive h-7 w-7"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          );
        }}
      />
      <PaginationBar
        page={table.page}
        perPage={table.perPage}
        total={table.total}
        onPageChange={table.setPage}
        disabled={table.isFetching}
      />

      <Sheet
        open={sheetMode !== null}
        onOpenChange={(next) => {
          if (!next) setSheetMode(null);
        }}
      >
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{sheetMode === "create" ? "New news" : "Edit news"}</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <NewsUploadForm
              {...(sheetMode && sheetMode !== "create" ? { n: sheetMode.record } : {})}
              onSaved={async () => {
                await table.invalidate();
                setSheetMode(null);
              }}
              onDeleted={async () => {
                await table.invalidate();
                setSheetMode(null);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null);
        }}
        title="Delete this news?"
        description={
          <>
            This removes the news titled{" "}
            <span className="text-foreground font-medium">
              {pendingDelete?.title ?? "this item"}
            </span>
            . This cannot be undone.
          </>
        }
        confirmLabel="Delete news"
        onConfirm={handleDelete}
      />
    </>
  );
}

interface TeacherRow extends SchemaRecord {
  id: string;
  name: string;
  login?: string;
  email?: string;
  username?: string;
  phone?: string;
}

function TeacherWorkspace({ fields }: { fields: FieldDef[] }) {
  const table = useEntityTable<TeacherRow>({
    collection: "teacher",
    searchField: "name",
    defaultSort: { field: "created", dir: "desc" },
  });

  const [sheetMode, setSheetMode] = useState<
    "create" | { mode: "edit"; record: TeacherRecord } | null
  >(null);
  const [pendingDelete, setPendingDelete] = useState<TeacherRow | null>(null);

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await pb.collection("teacher").delete(pendingDelete.id);
      toast.success(`Teacher [${pendingDelete.name}] deleted`);
      await table.invalidate();
    } catch (err) {
      toast.error("Could not delete teacher", { description: formatPbError(err) });
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SearchBox
          value={table.query}
          onChange={table.setQuery}
          placeholder="Search teachers by name…"
          className="w-full max-w-sm"
        />
        <Button size="sm" onClick={() => setSheetMode("create")} className="gap-2">
          <Plus className="h-3.5 w-3.5" />
          New teacher
        </Button>
      </div>
      <DataGrid
        fields={fields}
        records={table.records}
        loading={table.isLoading}
        currentSort={table.sort}
        onSortChange={table.setSort}
        hideFooter
        emptyHint={table.query ? "No teachers match this search." : "No teachers yet."}
        rowActions={(record) => {
          const row = record as TeacherRow;
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Edit ${row.name}`}
                onClick={() =>
                  setSheetMode({ mode: "edit", record: row as unknown as TeacherRecord })
                }
                className="text-muted-foreground hover:text-foreground h-7 w-7"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete ${row.name}`}
                onClick={() => setPendingDelete(row)}
                className="text-muted-foreground hover:text-destructive h-7 w-7"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          );
        }}
      />
      <PaginationBar
        page={table.page}
        perPage={table.perPage}
        total={table.total}
        onPageChange={table.setPage}
        disabled={table.isFetching}
      />

      <Sheet
        open={sheetMode !== null}
        onOpenChange={(next) => {
          if (!next) setSheetMode(null);
        }}
      >
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{sheetMode === "create" ? "New teacher" : "Edit teacher"}</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <TeacherUploadForm
              {...(sheetMode && sheetMode !== "create" ? { t: sheetMode.record } : {})}
              onSaved={async () => {
                await table.invalidate();
                setSheetMode(null);
              }}
              onDeleted={async () => {
                await table.invalidate();
                setSheetMode(null);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null);
        }}
        title="Delete this teacher?"
        description={
          <>
            This removes <span className="text-foreground font-medium">{pendingDelete?.name}</span>.
            The linked auth (`users`) row stays — re-create the teacher with the same login if
            needed. This cannot be undone.
          </>
        }
        confirmLabel="Delete teacher"
        onConfirm={handleDelete}
      />
    </>
  );
}

interface StudentRow extends SchemaRecord {
  id: string;
  name: string;
  batch?: string;
  login?: string;
  email?: string;
  username?: string;
  phone?: string;
}

function StudentWorkspace({
  fields,
  batchId,
  onBatchChange,
}: {
  fields: FieldDef[];
  batchId: string | undefined;
  onBatchChange: (id: string | undefined) => void;
}) {
  const queryClient = useQueryClient();
  const [csvMode, setCsvMode] = useState(false);
  const [exporting, setExporting] = useState(false);

  const table = useEntityTable<StudentRow>({
    collection: "student",
    extraFilter: batchId ? `batch = '${batchId}'` : undefined,
    searchField: "name",
    defaultSort: { field: "name", dir: "asc" },
    enabled: Boolean(batchId) && !csvMode,
  });

  const [sheetMode, setSheetMode] = useState<
    "create" | { mode: "edit"; record: StudentRecord } | null
  >(null);
  const [pendingDelete, setPendingDelete] = useState<StudentRow | null>(null);

  const { student: editStudentId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const handledEditRef = useRef<string | null>(null);

  // Auto-open the edit sheet when arriving via the student detail page's
  // "Edit details" deep-link (?student=<id>). Fetch the record directly so it
  // works regardless of which table page the student happens to be on.
  useEffect(() => {
    if (!editStudentId || handledEditRef.current === editStudentId) return;
    handledEditRef.current = editStudentId;
    pb.collection("student")
      .getOne<StudentRecord>(editStudentId)
      .then((record) => setSheetMode({ mode: "edit", record }))
      .catch((err) => toast.error("Could not open student", { description: formatPbError(err) }));
  }, [editStudentId]);

  // Drop the ?student= param once the deep-linked sheet is dismissed so a
  // refresh/back doesn't reopen it.
  function clearEditParam() {
    if (!editStudentId) return;
    void navigate({ search: (p) => ({ ...p, student: undefined }), replace: true });
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await pb.collection("student").delete(pendingDelete.id);
      toast.success(`Student [${pendingDelete.name}] deleted`);
      await table.invalidate();
    } catch (err) {
      toast.error("Could not delete student", { description: formatPbError(err) });
    }
  }

  // Export every student in the scoped batch as a CSV whose columns match the
  // upload template exactly (fetches the full set, not just the current page).
  async function handleExport() {
    if (!batchId || exporting) return;
    setExporting(true);
    try {
      // Fetch the batch name (for the filename) and the full student set in
      // parallel — getFullList pages through everything, not just page 1.
      const [batch, rows] = await Promise.all([
        pb.collection("batch").getOne<{ name: string }>(batchId, { fields: "name" }),
        pb.collection("student").getFullList<StudentExportRow>({
          filter: `batch = '${batchId}'`,
          sort: "name",
          fields: "name,dob,gender,email,phone,username,doj,caste",
        }),
      ]);
      if (rows.length === 0) {
        toast.info("No students in this batch to export.");
        return;
      }
      // Slugify the batch name for a safe, readable filename (spaces/punctuation
      // → hyphens), falling back to "batch" if it reduces to nothing.
      const slug =
        batch.name
          .trim()
          .replace(/[^a-zA-Z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "") || "batch";
      triggerCsvDownload(`students-${slug}.csv`, studentsToCsv(rows));
      toast.success(`Exported ${rows.length} student${rows.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Could not export students", { description: formatPbError(err) });
    } finally {
      setExporting(false);
    }
  }

  if (csvMode && batchId) {
    return (
      <>
        <BatchScopePicker batchId={batchId} onBatchChange={onBatchChange} label="Scope by batch" />
        <StudentCsvMode
          batchId={batchId}
          onDiscard={() => setCsvMode(false)}
          onCommitted={async () => {
            await queryClient.invalidateQueries({ queryKey: ["dashboard-upload", "student"] });
            setCsvMode(false);
          }}
        />
      </>
    );
  }

  return (
    <>
      <BatchScopePicker batchId={batchId} onBatchChange={onBatchChange} label="Scope by batch" />

      {!batchId ? (
        <GatedNotice>Pick a batch above to load students.</GatedNotice>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SearchBox
              value={table.query}
              onChange={table.setQuery}
              placeholder="Search students by name…"
              className="w-full max-w-sm"
            />
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
                className="gap-2"
              >
                <Download className="h-3.5 w-3.5" />
                {exporting ? "Exporting…" : "Export CSV"}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => setCsvMode(true)}
                className="gap-2"
              >
                <FileUp className="h-3.5 w-3.5" />
                Import from CSV
              </Button>
              <Button size="sm" onClick={() => setSheetMode("create")} className="gap-2">
                <Plus className="h-3.5 w-3.5" />
                New student
              </Button>
            </div>
          </div>
          <DataGrid
            fields={fields}
            records={table.records}
            loading={table.isLoading}
            currentSort={table.sort}
            onSortChange={table.setSort}
            hideFooter
            emptyHint={
              table.query ? "No students match this search." : "No students in this batch yet."
            }
            rowActions={(record) => {
              const row = record as StudentRow;
              return (
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${row.name}`}
                    onClick={() =>
                      setSheetMode({ mode: "edit", record: row as unknown as StudentRecord })
                    }
                    className="text-muted-foreground hover:text-foreground h-7 w-7"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${row.name}`}
                    onClick={() => setPendingDelete(row)}
                    className="text-muted-foreground hover:text-destructive h-7 w-7"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            }}
          />
          <PaginationBar
            page={table.page}
            perPage={table.perPage}
            total={table.total}
            onPageChange={table.setPage}
            disabled={table.isFetching}
          />

          <Sheet
            open={sheetMode !== null}
            onOpenChange={(next) => {
              if (!next) {
                setSheetMode(null);
                clearEditParam();
              }
            }}
          >
            <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
              <SheetHeader>
                <SheetTitle>{sheetMode === "create" ? "New student" : "Edit student"}</SheetTitle>
              </SheetHeader>
              <div className="mt-4">
                <StudentUploadForm
                  {...(sheetMode && sheetMode !== "create" ? { s: sheetMode.record } : {})}
                  {...(sheetMode === "create" && batchId ? { defaultBatchId: batchId } : {})}
                  onSaved={async () => {
                    await table.invalidate();
                    setSheetMode(null);
                    clearEditParam();
                  }}
                  onDeleted={async () => {
                    await table.invalidate();
                    setSheetMode(null);
                    clearEditParam();
                  }}
                />
              </div>
            </SheetContent>
          </Sheet>

          <ConfirmDialog
            open={pendingDelete !== null}
            onOpenChange={(next) => {
              if (!next) setPendingDelete(null);
            }}
            title="Delete this student?"
            description={
              <>
                This removes{" "}
                <span className="text-foreground font-medium">{pendingDelete?.name}</span>. The
                linked auth (`users`) row stays — re-create the student with the same login if
                needed. Report cards + marks for this student remain (no cascade). This cannot be
                undone.
              </>
            }
            confirmLabel="Delete student"
            onConfirm={handleDelete}
          />
        </>
      )}
    </>
  );
}
